/**
 * Remote Backup Backend
 * Ported from: innovation-claude/packages/storage/src/backends/remote_backup.rs
 *
 * Combines local and remote storage with backup and fallback:
 * - save: Writes to local (primary) and remote (backup)
 * - load: Tries local first, falls back to remote
 * - delete: Removes from both
 */

import { LocalBackend } from './LocalBackend'
import { RemoteBackend } from './RemoteBackend'

/**
 * RemoteBackupBackend - Local-first with remote backup
 *
 * Strategy:
 * - **Write**: Local (must succeed) + Remote (best effort)
 * - **Read**: Local first → Remote fallback → Cache to local
 * - **Delete**: Both local and remote
 *
 * Benefits:
 * - Fast reads (local cache)
 * - Resilient to network failures
 * - Cross-device sync (via remote)
 *
 * Ported from remote_backup.rs:39-147
 */
export class RemoteBackupBackend {
    private local: LocalBackend
    private remote: RemoteBackend

    constructor(
        baseUrl: string,
        getAuthToken: () => string,
        getUserId: () => string,
        getUsername: () => string
    ) {
        this.local = new LocalBackend()
        this.remote = new RemoteBackend(baseUrl, getAuthToken, getUserId, getUsername)
    }

    /**
     * NOTE: No updateAuth() method needed - uses functions that get latest values
     * Token renewal automatically reflected in next request
     */

    /**
     * Save to both local and remote
     * Local must succeed, remote is best-effort
     * Ported from remote_backup.rs:67-82
     */
    async save(key: string, json: string): Promise<void> {
        // Save to local (primary) - this MUST succeed
        await this.local.save(key, json)

        // Save to remote (backup) - best effort
        try {
            await this.remote.save(key, json)
        } catch (error) {
            console.warn(`Remote backup save failed for key '${key}':`, error)
            // Don't fail - local save succeeded
        }
    }

    /**
     * Load from local first, fallback to remote
     * If found in remote, cache to local
     * Ported from remote_backup.rs:88-120
     */
    async load(key: string): Promise<string | null> {
        // Try local first (fast path)
        const localData = await this.local.load(key)
        if (localData) {
            return localData
        }

        // Local miss - try remote fallback
        try {
            const remoteData = await this.remote.load(key)

            if (remoteData) {
                // Found in remote - cache to local for next time
                try {
                    await this.local.save(key, remoteData)
                } catch (error) {
                    console.warn(`Failed to cache remote data locally for key '${key}':`, error)
                    // Don't fail - we still have the remote data
                }

                return remoteData
            }

            return null  // Not found in either storage
        } catch (error) {
            // Remote also failed - return null
            console.warn(`Remote fallback failed for key '${key}':`, error)
            return null
        }
    }

    /**
     * Delete from both local and remote
     * Ported from remote_backup.rs:123-137
     */
    async delete(key: string): Promise<void> {
        // Delete from local
        await this.local.delete(key)

        // Delete from remote (best effort)
        try {
            await this.remote.delete(key)
        } catch (error) {
            console.warn(`Remote backup delete failed for key '${key}':`, error)
            // Don't fail - local delete succeeded
        }
    }
}
